import { useLazyGetPageByUrlQuery } from '@api/index';
import { EpiPage, LoadingStatus, PageRequest } from '@api/types';
import { useEditMode } from '@hooks';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const LazyLoginPage = lazy(() =>
  import('@components/pages').then((module) => ({ default: module.LoginPage }))
);
const LazyForbiddenPage = lazy(() =>
  import('@components/pages').then((module) => ({ default: module.ForbiddenPage }))
);
const LazyNotFoundPage = lazy(() =>
  import('@components/pages').then((module) => ({ default: module.NotFoundPage }))
);
const LazyServerErrorPage = lazy(() =>
  import('@components/pages').then((module) => ({ default: module.ServerErrorPage }))
);

const ContentPageLayout = lazy(() =>
  import('@components/layouts/ContentPageLayout').then((module) => ({
    default: module.ContentPageLayout,
  }))
);

export type PageContentLoaderProps = {
  page?: EpiPage;
  contentUrl?: string;
};

export const PageContentLoader = ({ page, contentUrl }: PageContentLoaderProps) => {
  const status: LoadingStatus = {
    isLoading: false,
    isError: false,
    isFetching: false,
    isSuccess: true,
  };
  return (
    <Suspense fallback={<div style={{ height: '60vh' }} aria-hidden='true' />}>
      {page ? (
        <ContentPageLayout page={page} status={status} />
      ) : (
        <PageContentUrlLoader contentUrl={contentUrl} />
      )}
    </Suspense>
  );
};

const PageContentUrlLoader = ({ contentUrl }: PageContentLoaderProps) => {
  const location = useLocation();
  const { subscribe } = useEditMode();

  const currentUrl = contentUrl || location.pathname;
  const prevUrlRef = useRef<string | null>(null);
  const [request, setRequest] = useState<PageRequest>({
    contentUrl: currentUrl,
    expand: '*',
  });

  const [trigger, response] = useLazyGetPageByUrlQuery();
  const { data, isLoading, isSuccess, isError, isFetching, error } = response;
  const page = data?.[0];
  const status = { isLoading, isSuccess, isError, isFetching };
  const statusCode = (data as any)?.status || (error as any)?.status;

  useEffect(() => {
    if (prevUrlRef.current !== currentUrl) {
      prevUrlRef.current = currentUrl;
      const newRequest = { contentUrl: currentUrl, expand: '*' };
      setRequest(newRequest);
      trigger(newRequest);
    }
  }, [currentUrl, trigger]);

  useEffect(() => {
    subscribe('contentSaved', () => trigger(request));
  }, [subscribe, trigger, request]);

  // Error handling
  if (isError && statusCode === 401) return <LazyLoginPage />;
  if (isError && statusCode === 403) return <LazyForbiddenPage />;
  if (isError && statusCode === 500) return <LazyServerErrorPage />;
  if (isSuccess && !page) return <LazyNotFoundPage />;

  if (isLoading || (isSuccess && page)) {
    return <ContentPageLayout page={page} status={status} />;
  }

  return null;
};
